'use client';

import CountdownBig from '../global/CountdownBig';
import Countdown from '../global/Countdown';
import heroBackground from '@/public/thank-you/hero.jpg';
import heroBackgroundMobile from '@/public/thank-you/hero-mobile.jpg';
import Image from 'next/image';
import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText, PortableTextBlock } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';

interface IHeroProps {
  enableCountdown: boolean;
  label: string;
  title: PortableTextBlock[];
  measuredRelease: string;
  numberOfPieces: string;
  privateReleaseLink: string;

  countdownTime: string;
}

export default function Hero({
  enableCountdown,
  label,
  title,
  measuredRelease,
  numberOfPieces,
  privateReleaseLink,
  countdownTime,
}: IHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  // Track scroll progress of the section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Transform scroll progress to translateY for parallax effect
  const videoY = useTransform(scrollYProgress, [0, 0.15, 1], [0, 0, 300]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1],
      },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="w-full  flex flex-col items-center lg:pt-[2rem]"
    >
      <div className="relative flex justify-between flex-col lg:max-w-[98%] lg:min-h-[115.125rem] min-h-[100rem] w-full overflow-clip px-0 lg:px-[6rem] pt-[16rem] lg:pt-[10rem] pb-[7rem] lg:items-start items-center">
        <motion.div
          className="mt-auto w-full relative z-10 flex flex-col"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={containerVariants}
        >
          <motion.div
            className="flex flex-col gap-[5rem] lg:w-[106.25rem] mb-0 lg:mb-[2rem] mx-auto lg:mx-0"
            variants={itemVariants}
          >
            <div
              className={cn(
                'flex flex-col items-center lg:items-start',
                enableCountdown === false
                  ? 'gap-[2rem] lg:gap-[2rem]'
                  : 'gap-[2.5rem] lg:gap-[3rem]',
              )}
            >
              <p
                className={cn(
                  'font-nunito text-white text-[1.625rem] font-[300] leading-[100%] tracking-[0.53625rem] uppercase lg:text-start text-center',
                  !enableCountdown &&
                    'bg-white/[8%] backdrop-blur-[1rem] border-gradient rounded-[1rem] px-[2.5rem] py-[2rem] lg:py-[2.25rem]',
                )}
              >
                {label}
              </p>

              {enableCountdown && (
                <motion.div variants={itemVariants} className="lg:hidden">
                  <Countdown enableDays enableStrings date={countdownTime} />
                </motion.div>
              )}

              <div className="w-[min(38.75rem,100%)] lg:w-full text-white prose-headings:font-nunito prose-em:!font-newsreader prose-headings:text-[4rem] lg:prose-headings:text-[6rem] prose-headings:leading-[120%] lg:prose-headings:leading-[140%] prose-headings:uppercase prose-headings:font-[200] prose-headings:mb-0 lg:text-start text-center">
                <PortableText value={title} components={richTextComponent} />
              </div>
            </div>
          </motion.div>

          {enableCountdown && (
            <motion.div
              variants={itemVariants}
              className="mb-[4rem] lg:block hidden"
            >
              <CountdownBig enableDays enableStrings date={countdownTime} />
            </motion.div>
          )}

          <motion.div
            className="w-full h-[52.375rem] relative pointer-events-none select-none lg:hidden"
            style={{ y: isMobile ? 0 : videoY }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 1.2,
              ease: [0.76, 0, 0.24, 1],
            }}
          >
            <Image
              src={heroBackgroundMobile}
              alt="Hero Background"
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              quality={100}
            />
          </motion.div>

          <motion.div
            className="flex lg:flex-row flex-col items-center gap-[1.5rem] font-nunito text-[1.625rem] font-[300] leading-[100%] tracking-[0.53625rem] uppercase px-[2rem] lg:px-0"
            variants={itemVariants}
          >
            <p className="text-white">{measuredRelease}</p>
            <span className="w-[0.375rem] h-[0.375rem] bg-white lg:block hidden"></span>
            <p className="text-white">{numberOfPieces}</p>
            <span className="w-[0.375rem] h-[0.375rem] bg-white lg:block hidden"></span>
            <p className="text-white">{privateReleaseLink}</p>
          </motion.div>
        </motion.div>

        <div className="absolute bg-gradient-to-t from-black to-transparent w-full h-[10rem] left-0 z-10 pointer-events-none select-none bottom-0 lg:hidden" />
        <div
          style={{
            background:
              'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
          }}
          className="absolute lg:w-[85rem] w-full opacity-[0.7] h-1/2 lg:h-full left-0  z-[1] pointer-events-none select-none bottom-0 lg:block hidden"
        ></div>
        <div
          style={{
            background:
              'linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, #000 100%)',
          }}
          className="absolute lg:w-[85rem] w-full opacity-[0.7] h-1/2 lg:h-full left-0  z-[1] pointer-events-none select-none bottom-0 lg:block hidden"
        ></div>

        <motion.div
          className="absolute inset-0 z-0 pointer-events-none select-none hidden lg:block"
          style={{ y: videoY }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 1.2,
            ease: [0.76, 0, 0.24, 1],
          }}
        >
          <Image
            src={heroBackground}
            alt="Hero Background"
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            quality={100}
          />
        </motion.div>
      </div>
    </section>
  );
}
